import { useEffect } from 'react';
import { Descriptions, Form, Input, Modal, message } from 'antd';

import { VSCPartData } from '../../../interfaces/VersaBuilt.Interface';
import { useDuplicateVSCPartDataMutation } from '../../../redux/api/versabuilt';
import { extractApiError } from '../../../utils/apiError';

interface RobotProgramDuplicateModalProps {
	orderLineItemId: string;
	program: VSCPartData | null;
	programs: VSCPartData[];
	onClose: () => void;
}

const RobotProgramDuplicateModal = ({
	orderLineItemId,
	program,
	programs,
	onClose,
}: RobotProgramDuplicateModalProps) => {
	const [form] = Form.useForm<{ name: string }>();
	const [messageApi, messageContext] = message.useMessage();
	const [duplicateVSCPartData, { isLoading }] =
		useDuplicateVSCPartDataMutation();

	useEffect(() => {
		if (program) {
			form.resetFields();
		}
	}, [program]);

	const description = program?.json_data?.description ?? null;

	// Mirrors the backend upsert key: a copy sharing name and description
	// with another program here would be overwritten by the next export.
	const takenNames = new Set(
		programs
			.filter((p) => (p.json_data?.description ?? null) === description)
			.map((p) => p.json_data?.name)
	);

	const handleSubmit = async ({ name }: { name: string }) => {
		if (!program) {
			return;
		}
		try {
			await duplicateVSCPartData({
				id: program.id,
				orderLineItemId,
				name: name.trim(),
			}).unwrap();
			messageApi.success(`Duplicated as ${name.trim()}`);
			onClose();
		} catch (e) {
			messageApi.error(extractApiError(e));
		}
	};

	return (
		<>
			<Modal
				title="Duplicate Robot Program"
				open={!!program}
				onCancel={onClose}
				okText="Duplicate"
				okButtonProps={{ htmlType: 'submit', form: 'duplicate-robot-program', loading: isLoading }}
				cancelButtonProps={{ disabled: isLoading }}
				destroyOnClose
				centered
			>
				<Descriptions
					size="small"
					column={1}
					className="mb-4"
					items={[
						{ key: 'source', label: 'Copying', children: program?.json_data?.name ?? '—' },
						{ key: 'description', label: 'Description', children: description ?? '—' },
					]}
				/>
				<Form
					form={form}
					id="duplicate-robot-program"
					layout="vertical"
					onFinish={handleSubmit}
					disabled={isLoading}
				>
					<Form.Item
						name="name"
						label="New name"
						rules={[
							{ required: true, whitespace: true, message: 'Enter a name for the copy' },
							{
								validator: (_, value?: string) =>
									value && takenNames.has(value.trim())
										? Promise.reject(
												new Error(
													'A program with this name and description already exists on this line item'
												)
										  )
										: Promise.resolve(),
							},
						]}
					>
						<Input autoFocus placeholder={program?.json_data?.name} />
					</Form.Item>
				</Form>
			</Modal>
			{messageContext}
		</>
	);
};

export default RobotProgramDuplicateModal;
